const MIN_SCORE = 35;
const DETECT_TIMEOUT_MS = 7000;

class MigratieScan extends HTMLElement {
  constructor() {
    super();
    this.domain = '';
    this.scanUrl = '';
    this.platform = null;
    this.platformName = '';
    this.platformShortName = null;
    this.platformConfidence = '';
    this.scanTimeout = null;
    this.detectCache = {};
  }

  pushDataLayer(eventName, extraData = {}) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      scan_domain: this.domain || undefined,
      scan_platform: this.platform || undefined,
      ...extraData,
    });
  }

  connectedCallback() {
    this.storageKey = this.dataset.storageKey || 'migratieScanLead';
    this.scanDuration = parseInt(this.dataset.scanDuration, 10) || 1800;
    this.fingerprintEndpoint =
      this.dataset.fingerprintEndpoint || 'https://platform-detector.shopnotch.workers.dev';
    this.platformFallback = this.dataset.platformFallback || 'je huidige platform';

    this.querySelectorAll('[data-migratie-scan-action]').forEach((control) => {
      control.addEventListener('click', this.onActionClick.bind(this));
    });

    const form = this.querySelector('.migratie-scan__form');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }

    const urlInput = this.querySelector('[data-migratie-scan="url-input"]');
    if (urlInput) {
      urlInput.addEventListener('keydown', this.onUrlKeydown.bind(this));
    }

    const initialStep = this.dataset.initialStep;
    const returnedFromSuccessfulSubmit =
      initialStep === 'success' || this.isContactFormSuccessReturn();

    if (returnedFromSuccessfulSubmit) {
      this.restoreLeadFromStorage();
      this.showStep(4);
      this.pushDataLayer('migratie_scan_step4_completed');
      this.cleanContactFormSuccessUrl();
    } else if (initialStep === 'error') {
      this.restoreScanContextFromStorage();
      this.syncFormHiddenFields();
      this.showStep(3);
    } else {
      this.showStep(0);
    }
  }

  disconnectedCallback() {
    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }
  }

  onActionClick(event) {
    const control = event.currentTarget;
    const action = control.dataset.migratieScanAction;

    if (action === 'go-step') {
      const step = parseInt(control.dataset.step, 10);
      if (step === 1) {
        this.pushDataLayer('migratie_scan_step1_started');
      } else if (step === 3) {
        this.pushDataLayer('migratie_scan_step3_form_opened');
      }
      this.showStep(step);
      return;
    }

    if (action === 'start-scan') {
      this.startScan();
    }
  }

  onUrlKeydown(event) {
    if (event.key !== 'Enter') {
      return;
    }

    event.preventDefault();
    this.startScan();
  }

  onFormSubmit() {
    const nameInput = this.querySelector('[data-migratie-scan="name-input"]');
    const emailInput = this.querySelector('[data-migratie-scan="email-input"]');

    const name = nameInput ? nameInput.value.trim() : '';
    const email = emailInput ? emailInput.value.trim() : '';

    if (!name || !email) {
      return;
    }

    this.restoreScanContextFromStorage();
    this.syncFormHiddenFields();

    const payload = {
      name,
      email,
      domain: this.domain,
      scanUrl: this.scanUrl,
      platform: this.platform,
      platformName: this.platformName,
    };

    try {
      sessionStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch (e) {
      /* sessionStorage unavailable */
    }

    this.pushDataLayer('migratie_scan_step3_form_submitted', {
      scan_email: email,
    });
  }

  normalizeDomain(input) {
    const raw = String(input == null ? '' : input).trim();
    if (!raw) {
      throw new Error('Vul een webadres in');
    }

    const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw)
      ? raw.replace(/^[a-z][a-z0-9+.-]*:\/\//i, 'https://')
      : `https://${raw}`;

    let host;
    try {
      host = new URL(withScheme).hostname.toLowerCase().replace(/\.+$/, '');
    } catch (e) {
      throw new Error('Dat lijkt geen geldig webadres');
    }

    if (host.indexOf('.') === -1 || host.charAt(0) === '.') {
      throw new Error('Dat lijkt geen geldig webadres');
    }

    return host;
  }

  summarizeDetection(data) {
    if (!data) {
      return {
        platform: null,
        name: null,
        displayName: 'niet automatisch herkend',
        confidence: null,
        isShopify: false,
      };
    }

    if (data.ok === false) {
      return {
        platform: null,
        name: null,
        displayName: data.error || 'Site niet bereikbaar',
        confidence: null,
        isShopify: false,
      };
    }

    if (data.platform && data.score >= MIN_SCORE) {
      const extra =
        data.cms && data.cms.id !== data.platform ? ` (op ${data.cms.name})` : '';
      const displayName = data.name + extra;
      return {
        platform: data.platform,
        name: data.name,
        displayName,
        confidence: data.confidence || null,
        isShopify: String(data.platform).toLowerCase() === 'shopify',
      };
    }

    if (data.status >= 500) {
      return {
        platform: null,
        name: null,
        displayName: 'Site niet bereikbaar — bestaat dit domein wel?',
        confidence: null,
        isShopify: false,
      };
    }

    if (data.blocked) {
      return {
        platform: null,
        name: null,
        displayName: 'De site blokkeert geautomatiseerde bezoeken, dus we konden niet kijken',
        confidence: null,
        isShopify: false,
      };
    }

    if (data.platform) {
      return {
        platform: data.platform,
        name: data.name,
        displayName: `Geen zekerheid — mogelijk ${data.name}, maar te weinig bewijs`,
        confidence: data.confidence || null,
        isShopify: false,
      };
    }

    if (data.frontend) {
      return {
        platform: null,
        name: null,
        displayName: `Geen bekend platform — eigen of headless front-end op ${data.frontend.name}`,
        confidence: null,
        isShopify: false,
      };
    }

    return {
      platform: null,
      name: null,
      displayName: 'niet automatisch herkend',
      confidence: null,
      isShopify: false,
    };
  }

  fetchPlatform(host) {
    if (this.detectCache[host]) {
      return Promise.resolve(this.detectCache[host]);
    }

    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
    }, DETECT_TIMEOUT_MS);

    const endpoint = this.fingerprintEndpoint.replace(/\/$/, '');

    return fetch(`${endpoint}/?url=${encodeURIComponent(host)}`, { signal: controller.signal })
      .then((res) => {
        clearTimeout(timer);
        if (!res.ok && res.status !== 502) {
          return null;
        }
        return res.json();
      })
      .catch(() => null)
      .then((data) => {
        const summary = this.summarizeDetection(data);
        this.detectCache[host] = summary;
        return summary;
      });
  }

  startScan() {
    const urlInput = this.querySelector('[data-migratie-scan="url-input"]');
    if (!urlInput) {
      return;
    }

    const url = urlInput.value.trim();
    if (!url) {
      urlInput.focus();
      return;
    }

    let host;
    try {
      host = this.normalizeDomain(url);
    } catch (e) {
      urlInput.focus();
      return;
    }

    this.scanUrl = url;
    this.domain = host;
    this.persistScanContext();
    this.capturePartialLead();

    this.querySelectorAll('[data-migratie-scan="scan-domain"], [data-migratie-scan="res-domain"]').forEach(
      (el) => {
        el.textContent = this.domain;
      }
    );

    const scanningPanel = this.querySelector('[data-migratie-scan-panel="scanning"]');
    const resultsPanel = this.querySelector('[data-migratie-scan-panel="results"]');
    const migVariant = this.querySelector('[data-migratie-scan-panel="mig"]');
    const shopifyVariant = this.querySelector('[data-migratie-scan-panel="shopify"]');

    if (scanningPanel) {
      scanningPanel.hidden = false;
    }
    if (resultsPanel) {
      resultsPanel.hidden = true;
    }
    if (migVariant) {
      migVariant.hidden = true;
    }
    if (shopifyVariant) {
      shopifyVariant.hidden = true;
    }

    this.showStep(2);
    this.pushDataLayer('migratie_scan_step2_url_submitted');

    if (this.scanTimeout) {
      clearTimeout(this.scanTimeout);
    }

    const started = Date.now();

    this.fetchPlatform(host).then((summary) => {
      const wait = Math.max(0, this.scanDuration - (Date.now() - started));
      this.scanTimeout = setTimeout(() => {
        this.applyDetectionResult(summary);
        if (scanningPanel) {
          scanningPanel.hidden = true;
        }
        if (resultsPanel) {
          resultsPanel.hidden = false;
        }
        this.pushDataLayer('migratie_scan_step2_results_shown', {
          scan_platform: summary.platform || undefined,
        });
      }, wait);
    });
  }

  applyDetectionResult(summary) {
    this.platform = summary.platform;
    this.platformName = summary.displayName;
    this.platformShortName = summary.name || null;
    this.platformConfidence = summary.confidence;
    this.persistScanContext();

    const migVariant = this.querySelector('[data-migratie-scan-panel="mig"]');
    const shopifyVariant = this.querySelector('[data-migratie-scan-panel="shopify"]');
    const platName = this.querySelector('[data-migratie-scan="plat-name"]');

    if (summary.isShopify) {
      if (migVariant) {
        migVariant.hidden = true;
      }
      if (shopifyVariant) {
        shopifyVariant.hidden = false;
      }
    } else {
      if (migVariant) {
        migVariant.hidden = false;
      }
      if (shopifyVariant) {
        shopifyVariant.hidden = true;
      }
      if (platName) {
        platName.textContent = summary.name || summary.displayName;
      }
      this.updatePlatformSpans(summary.name);
    }

    this.syncFormHiddenFields();
  }

  updatePlatformSpans(detectedName) {
    const fallback = this.platformFallback;
    this.querySelectorAll('[data-migratie-scan="pf-plat"]').forEach((el) => {
      if (detectedName) {
        el.textContent = detectedName;
        el.classList.add('migratie-scan__pf-plat--known');
      } else {
        el.textContent = fallback;
        el.classList.remove('migratie-scan__pf-plat--known');
      }
    });
  }

  getSubmittedUrl() {
    if (this.scanUrl) {
      return this.scanUrl.trim();
    }

    if (this.domain) {
      return `https://${this.domain}`;
    }

    return '';
  }

  syncFormHiddenFields() {
    const hiddenUrl = this.querySelector('[data-migratie-scan="hidden-url"]');
    const hiddenPlatform = this.querySelector('[data-migratie-scan="hidden-platform"]');
    const hiddenBody = this.querySelector('[data-migratie-scan="hidden-body"]');
    const url = this.getSubmittedUrl();
    const platformLabel = this.platformName || this.platform || 'onbekend';

    if (hiddenUrl) {
      hiddenUrl.value = url;
    }

    if (hiddenPlatform) {
      hiddenPlatform.value = platformLabel;
    }

    if (hiddenBody) {
      hiddenBody.value = `Shopify Migratiescan — ${this.domain || 'onbekend'} (${platformLabel})`;
    }
  }

  showStep(stepIndex) {
    this.querySelectorAll('[data-migratie-scan-step]').forEach((step) => {
      const index = parseInt(step.dataset.migratieScanStep, 10);
      const isActive = index === stepIndex;
      step.hidden = !isActive;
      step.classList.toggle('agentic-scan__step--active', isActive);
      step.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    const section = this.closest('.shopify-section') || this;
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const activeStep = this.querySelector(`[data-migratie-scan-step="${stepIndex}"]:not([hidden])`);
    if (!activeStep) {
      return;
    }

    const focusable = activeStep.querySelector(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable && stepIndex > 0) {
      focusable.focus({ preventScroll: true });
    }

    if (stepIndex === 3) {
      this.restoreScanContextFromStorage();
      this.syncFormHiddenFields();
    }
  }

  getDomain(url) {
    try {
      return this.normalizeDomain(url);
    } catch (e) {
      return url;
    }
  }

  persistScanContext() {
    try {
      const existing = sessionStorage.getItem(this.storageKey);
      const data = existing ? JSON.parse(existing) : {};
      data.domain = this.domain;
      data.scanUrl = this.scanUrl;
      data.platform = this.platform;
      data.platformName = this.platformName;
      data.platformShortName = this.platformShortName;
      sessionStorage.setItem(this.storageKey, JSON.stringify(data));
    } catch (e) {
      /* sessionStorage unavailable */
    }
  }

  capturePartialLead() {
    const endpoint = (this.dataset.captureEndpoint || '').trim();
    if (!endpoint) {
      return;
    }

    const payload = JSON.stringify({
      domain: this.domain,
      scanUrl: this.scanUrl,
      source: 'migratie-scan',
    });

    const url = endpoint.replace(/\/$/, '');

    // text/plain avoids CORS preflight; application/json + sendBeacon often fails silently cross-origin
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
      body: payload,
      keepalive: true,
      mode: 'cors',
    }).catch(() => {
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
        }
      } catch (e) {
        /* ignore */
      }
    });
  }

  restoreScanContextFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) {
        return;
      }
      const data = JSON.parse(raw);
      if (data.domain) {
        this.domain = data.domain;
      }
      if (data.scanUrl) {
        this.scanUrl = data.scanUrl;
      }
      if (data.platform) {
        this.platform = data.platform;
      }
      if (data.platformName) {
        this.platformName = data.platformName;
      }
      if (data.platformShortName) {
        this.platformShortName = data.platformShortName;
      }
      this.querySelectorAll(
        '[data-migratie-scan="res-domain"], [data-migratie-scan="scan-domain"]'
      ).forEach((el) => {
        el.textContent = this.domain;
      });
      this.updatePlatformSpans(this.platformShortName);
    } catch (e) {
      /* ignore */
    }
  }

  isContactFormSuccessReturn() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('contact_posted') !== 'true') {
      return false;
    }

    const form = this.querySelector('.migratie-scan__form');
    if (!form?.id) {
      return true;
    }

    const hashId = window.location.hash.replace(/^#/, '');
    return hashId === '' || hashId === form.id;
  }

  cleanContactFormSuccessUrl() {
    try {
      const url = new URL(window.location.href);
      if (!url.searchParams.has('contact_posted')) {
        return;
      }
      url.searchParams.delete('contact_posted');
      const cleaned = `${url.pathname}${url.search}${url.hash}`;
      window.history.replaceState({}, '', cleaned);
    } catch (e) {
      /* ignore */
    }
  }

  restoreLeadFromStorage() {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      if (!raw) {
        return;
      }
      const data = JSON.parse(raw);
      const firstName = data.name ? data.name.split(' ')[0] : '';
      const thankName = this.querySelector('[data-migratie-scan="thank-name"]');
      const thankEmail = this.querySelector('[data-migratie-scan="thank-email"]');
      const thankDomain = this.querySelector('[data-migratie-scan="thank-domain"]');

      if (thankName && firstName) {
        thankName.textContent = `${firstName}!`;
      }
      if (thankEmail && data.email) {
        thankEmail.textContent = data.email;
      }
      if (thankDomain && data.domain) {
        thankDomain.textContent = data.domain;
      }

      sessionStorage.removeItem(this.storageKey);
    } catch (e) {
      /* ignore */
    }
  }
}

if (!customElements.get('migratie-scan')) {
  customElements.define('migratie-scan', MigratieScan);
}
